import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { useSubscriptionLimits } from '../../hooks/useSubscriptionLimits';
import EmployerHeaderNav from '../../components/navigation/EmployerHeaderNav';
import SubscriptionLimitBanner from '../../components/subscription/SubscriptionLimitBanner';

// Custom hook for form state management
import { useJobPostingForm } from './useJobPostingForm';

// Step components
import { Step1JobDetails } from './steps/Step1JobDetails';
import { Step2Requirements } from './steps/Step2Requirements';
import { Step3Compensation } from './steps/Step3Compensation';
import { Step4ReviewPublish } from './steps/Step4ReviewPublish';

// UI components
import { PageHeader } from './components/PageHeader';
import { ProgressSteps } from './components/ProgressSteps';
import { SuccessMessage, ErrorMessage } from './components/Messages';
import { FormNavigation } from './components/FormNavigation';

/**
 * Job Posting Page Component
 * Refactored for better performance and maintainability
 * 
 * Features:
 * - Multi-step form with validation
 * - Draft saving capability
 * - Job editing support
 * - Subscription limit checking
 * - Performance optimized with memoized sub-components
 */
const JobPostingPage = () => {
  const { user, userProfile } = useAuth();
  const { canPerformAction, recordAction } = useSubscriptionLimits();
  const navigate = useNavigate();
  const { jobId } = useParams();

  // Use custom hook for all form logic
  const {
    jobData,
    loading,
    errors,
    successMessage,
    currentStep,
    isEditMode,
    loadJobData,
    handleNext,
    handlePrevious,
    updateJobData,
    toggleBenefit,
    saveDraft,
    publishJob,
    setErrors,
    setSuccessMessage,
    setAIGenerated
  } = useJobPostingForm(jobId, user, userProfile, () => {});

  // Load job data if editing
  useEffect(() => {
    if (jobId) {
      loadJobData(jobId);
    }
  }, [jobId, loadJobData]);

  /**
   * Handle save draft action
   */
  const handleSaveDraft = async () => {
    const result = await saveDraft('draft');
    
    if (result.success && result.isNewJob) {
      // Use public slug if available for cleaner URLs
      const urlParam = result.publicSlug || result.jobId;
      navigate(`/job-posting/${urlParam}`, { replace: true });
    }
  };

  /**
   * Handle publish action
   */
  const handlePublish = async () => {
    const result = await publishJob(canPerformAction, recordAction);
    
    if (result.success) {
      setTimeout(() => {
        navigate('/employer-dashboard');
      }, 2000);
    }
  };

  /**
   * Render step content based on current step
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1JobDetails
            jobData={jobData}
            errors={errors}
            onUpdate={updateJobData}
          />
        );
      case 2:
        return (
          <Step2Requirements
            jobData={jobData}
            errors={errors}
            onUpdate={updateJobData}
          />
        );
      case 3:
        return (
          <Step3Compensation
            jobData={jobData}
            errors={errors}
            onUpdate={updateJobData}
            onBenefitToggle={toggleBenefit}
          />
        );
      case 4:
        return <Step4ReviewPublish jobData={jobData} />;
      default:
        return null;
    }
  };

  // Early return if no user
  if (!user || !userProfile) {
    return (
      <div className="min-h-screen bg-background">
        <EmployerHeaderNav />
        <main className="pt-16 pb-20 md:pb-8">
          <div className="content-container">
            <div className="text-center py-12">
              <p className="text-text-secondary">Loading...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <EmployerHeaderNav />

      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container">
          {/* Header */}
          <PageHeader
            isEditMode={isEditMode}
            userProfile={userProfile}
            onAIGenerated={setAIGenerated}
          />

          {/* Subscription Limit Banner */}
          <SubscriptionLimitBanner actionType="jobs" showIfUnlimited={false} />

          {/* Expired Job Notice */}
          {isEditMode && jobData.status === 'expired' && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <span className="text-red-500 mt-0.5">⏰</span>
              <div>
                <p className="text-sm font-medium text-red-800">This job posting has expired</p>
                <p className="text-sm text-red-600 mt-1">
                  The application deadline has passed. To reactivate this job, extend the application deadline to a future date and save. 
                  The job will be submitted for a brief re-evaluation before going live again.
                </p>
              </div>
            </div>
          )}

          {/* Progress Steps */}
          <ProgressSteps currentStep={currentStep} /> 

          {/* Messages */}
          <SuccessMessage message={successMessage} />
          <ErrorMessage message={errors.general} />

          {/* Main Content */}
          <div className="bg-surface rounded-lg border border-border p-6">
            {renderStepContent()}

            {/* Navigation Buttons */}
            <FormNavigation
              currentStep={currentStep}
              loading={loading}
              isEditMode={isEditMode}
              onPrevious={handlePrevious}
              onSaveDraft={handleSaveDraft}
              onNext={handleNext}
              onPublish={handlePublish}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default JobPostingPage;
